import { useState } from "react";
import { NavLink, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  LayoutDashboard,
  HeartPulse,
  Users,
  ShieldAlert,
  Grid3X3,
  Search,
  ChevronLeft,
  Download,
  Hexagon,
  GraduationCap,
  DatabaseZap,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";

import { resolveRole, getMentorPLG, isManagerOrAbove } from "@/lib/roles";
import { ROUTES } from "@/lib/routes";

type NavItem = { label: string; path: string; icon: any; adminOnly?: boolean };

const allNavItems: NavItem[] = [
  { label: "Executive Summary", path: ROUTES.HOME, icon: LayoutDashboard, adminOnly: true },
  { label: "Program Health", path: ROUTES.HEALTH, icon: HeartPulse, adminOnly: true },
  { label: "Mentor Operations", path: ROUTES.MENTORS, icon: Users, adminOnly: true },
  { label: "Learner Risk", path: ROUTES.RISK, icon: ShieldAlert, adminOnly: true },
  { label: "PLG Directory", path: ROUTES.PLGS, icon: Grid3X3, adminOnly: true },
  { label: "Learner Search", path: ROUTES.LEARNERS, icon: Search, adminOnly: true },
  { label: "Mentor View", path: ROUTES.MENTOR_SELECT, icon: GraduationCap },
  { label: "Data Quality", path: ROUTES.DATA_QUALITY, icon: DatabaseZap, adminOnly: true },
];

export function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { user, role: claimsRole, memberships, logout } = useAuth();
  const role = resolveRole(claimsRole, user?.email);
  const mentorPlg = getMentorPLG(memberships);

  const navItems = isManagerOrAbove(role)
    ? allNavItems
    : allNavItems.filter((i) => !i.adminOnly).map((i) =>
        i.path === ROUTES.MENTOR_SELECT && mentorPlg ? { ...i, path: ROUTES.MENTOR_VIEW(mentorPlg), label: "My PLG" } : i
      );

  const pageTitle = navItems.find((i) => i.path === location.pathname)?.label
    || (location.pathname.startsWith(ROUTES.PLGS) && location.pathname !== ROUTES.PLGS ? "PLG Detail"
    : location.pathname.startsWith("/mentor/") ? "My PLG Dashboard"
    : "Dashboard");

  return (
    <div className="flex min-h-screen w-full">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300 ${
          collapsed ? "w-16" : "w-60"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-sidebar-border">
          <Hexagon className="w-7 h-7 text-sidebar-primary shrink-0" />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-sm font-bold text-sidebar-accent-foreground leading-tight">Africa K8s Pulse</span>
              <span className="text-[10px] text-sidebar-foreground leading-tight">Andela x CNCF Program</span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path === "/plgs" && location.pathname.startsWith("/plgs/"));
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                }`}
              >
                <item.icon className="w-4.5 h-4.5 shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* User */}
        {user && (
          <div className="px-3 py-2 border-t border-sidebar-border">
            <div className="flex items-center gap-2">
              {user.photoURL && <img src={user.photoURL} alt="" className="w-6 h-6 rounded-full shrink-0" />}
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-sidebar-accent-foreground truncate">{user.displayName}</p>
                  <p className="text-[9px] text-sidebar-foreground truncate">{user.email}</p>
                </div>
              )}
              <button onClick={logout} className="shrink-0 text-sidebar-foreground hover:text-sidebar-accent-foreground" title="Sign out">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Collapse */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center h-10 border-t border-sidebar-border text-sidebar-foreground hover:text-sidebar-accent-foreground transition-colors"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${collapsed ? "ml-16" : "ml-60"}`}>
        {/* Top ribbon */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b h-14 flex items-center justify-between px-6">
          <h1 className="text-lg font-semibold">{pageTitle}</h1>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5">
              <Download className="w-3.5 h-3.5" />
              Export
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
